"use client";

// =============================================================================
// UsageSections — shared collapsible usage breakdown
// =============================================================================
//
// Renders usage as a list of collapsible sections, one per section/mode, in a
// fixed order: tiers, legacy names, Judging, Audio, Other.
//
// - Collapsed by default. Each header shows: section name · N req · N searches · $cost.
// - Clicking a header expands a per-model breakdown (one row per by_model
//   entry: model label, requests, searches, cost).
// - Only sections with usage in scope are shown; "No usage" when none.
//
// Cross-platform parity: an iOS agent renders the identical UI. Section
// order, collapse behaviour, and labels MUST stay consistent across both.
// =============================================================================

import { useState, Fragment } from "react";
import type { UsageRow, UsageSectionRow } from "@/app/lib/api";

// -----------------------------------------------------------------------------
// Section order + display names (canonical — keep in sync with iOS + server).
// -----------------------------------------------------------------------------

// Fixed render order. Any unknown section value is folded into "other".
export const SECTION_ORDER = [
  "instant",
  "low",
  "medium",
  "high",
  "max",
  "fast",
  "thinking",
  "pro",
  "deep_research",
  "judging",
  "audio",
  "other",
] as const;

const SECTION_DISPLAY_NAME: Record<string, string> = {
  instant: "Instant",
  low: "Low",
  medium: "Medium",
  high: "High",
  max: "Max",
  fast: "Fast",
  thinking: "Thinking",
  pro: "Pro",
  deep_research: "Deep Research",
  judging: "Judging",
  audio: "Audio",
  other: "Other",
};

function sectionDisplayName(section: string): string {
  return SECTION_DISPLAY_NAME[section] || section;
}

// -----------------------------------------------------------------------------
// Formatting helpers (local — mirrors the formatters in UsagePanel/AdminPanel).
// -----------------------------------------------------------------------------

function formatCost(value: number | undefined | null): string {
  if (value === undefined || value === null) return "$0.00";
  return `$${Number(value).toFixed(2)}`;
}

// -----------------------------------------------------------------------------
// Normalized section shape consumed by the renderer.
// -----------------------------------------------------------------------------

export interface UsageModelEntry {
  /** Display label for the model (already resolved). */
  label: string;
  requests: number;
  web_search_count: number;
  cost_usd: number;
}

export interface UsageSectionEntry {
  /** Canonical section key (fast / thinking / … / other). */
  section: string;
  requests: number;
  web_search_count: number;
  cost_usd: number;
  /** Per-model breakdown shown when the section is expanded. */
  models: UsageModelEntry[];
}

// -----------------------------------------------------------------------------
// Builders — turn the two server shapes into UsageSectionEntry[].
// -----------------------------------------------------------------------------

/** Canonicalize a raw section/mode value to one of SECTION_ORDER. */
function canonSection(value: string): string {
  return (SECTION_ORDER as readonly string[]).includes(value) ? value : "other";
}

/**
 * Build ordered sections from a summary month's `sections[]` + `rows[]`.
 * `sections[]` gives the per-section totals; `rows[]` (with `by_model`)
 * supplies the per-model breakdown grouped by the row's `mode`.
 *
 * Used by the user-facing Usage panel and the admin-wide overview, both of
 * which receive the `/usage/me/summary` · `/admin/usage/summary` shape.
 */
export function buildSectionsFromSummary(
  rows: UsageRow[] | undefined | null,
  sections: UsageSectionRow[] | undefined | null,
  modelLabelFor: (provider: string, mode: string, model: string) => string
): UsageSectionEntry[] {
  // Per-section model accumulator: section -> model label -> {req, searches, cost}.
  const modelsBySection = new Map<string, Map<string, UsageModelEntry>>();
  for (const r of rows || []) {
    const sect = canonSection(r.mode);
    let m = modelsBySection.get(sect);
    if (!m) {
      m = new Map();
      modelsBySection.set(sect, m);
    }
    const byModel = r.by_model || [];
    if (byModel.length > 0) {
      for (const bm of byModel) {
        const label = modelLabelFor(r.provider, r.mode, bm.model);
        const existing = m.get(label);
        if (existing) {
          existing.requests += bm.requests;
          existing.web_search_count += bm.web_search_count ?? 0;
          existing.cost_usd += bm.cost_usd;
        } else {
          m.set(label, { label, requests: bm.requests, web_search_count: bm.web_search_count ?? 0, cost_usd: bm.cost_usd });
        }
      }
    } else {
      // Pre-overhaul bucket — no by_model. Fall back to one synthetic
      // per-row model line so the section still expands to something.
      const label = modelLabelFor(r.provider, r.mode, "");
      const existing = m.get(label);
      if (existing) {
        existing.requests += r.requests;
        existing.web_search_count += r.web_search_count ?? 0;
        existing.cost_usd += r.cost_usd;
      } else {
        m.set(label, { label, requests: r.requests, web_search_count: r.web_search_count ?? 0, cost_usd: r.cost_usd });
      }
    }
  }

  // Per-section totals. Prefer the server `sections[]`; fall back to summing
  // rows if `sections[]` is absent (older server payloads).
  const totalsBySection = new Map<string, { requests: number; web_search_count: number; cost_usd: number }>();
  if (sections && sections.length > 0) {
    for (const s of sections) {
      const sect = canonSection(s.section);
      const t = totalsBySection.get(sect) || { requests: 0, web_search_count: 0, cost_usd: 0 };
      t.requests += s.requests;
      t.web_search_count += s.web_search_count ?? 0;
      t.cost_usd += s.cost_usd;
      totalsBySection.set(sect, t);
    }
  } else {
    for (const r of rows || []) {
      const sect = canonSection(r.mode);
      const t = totalsBySection.get(sect) || { requests: 0, web_search_count: 0, cost_usd: 0 };
      t.requests += r.requests;
      t.web_search_count += r.web_search_count ?? 0;
      t.cost_usd += r.cost_usd;
      totalsBySection.set(sect, t);
    }
  }

  return assembleOrdered(totalsBySection, modelsBySection);
}

/**
 * Build ordered sections from a per-user usage map for ONE month:
 * `{provider: {mode: {requests, cost_usd, by_model{}}}}`.
 *
 * Used by the admin per-user expander, which receives `/admin/usage/{email}`
 * narrowed to a single month.
 */
export function buildSectionsFromUserMonth(
  byProvider: Record<string, Record<string, any>> | undefined | null,
  modelLabelFor: (provider: string, mode: string, model: string) => string
): UsageSectionEntry[] {
  const modelsBySection = new Map<string, Map<string, UsageModelEntry>>();
  const totalsBySection = new Map<string, { requests: number; web_search_count: number; cost_usd: number }>();

  for (const provider of Object.keys(byProvider || {})) {
    const modes = byProvider![provider] || {};
    for (const mode of Object.keys(modes)) {
      const stats = modes[mode];
      if (!stats || typeof stats !== "object") continue;
      const sect = canonSection(mode);
      const reqs = stats.requests ?? 0;
      const searches = stats.web_search_count ?? 0;
      const cost = stats.cost_usd ?? stats.cost ?? 0;

      const t = totalsBySection.get(sect) || { requests: 0, web_search_count: 0, cost_usd: 0 };
      t.requests += reqs;
      t.web_search_count += searches;
      t.cost_usd += cost;
      totalsBySection.set(sect, t);

      let m = modelsBySection.get(sect);
      if (!m) {
        m = new Map();
        modelsBySection.set(sect, m);
      }
      // by_model is a {model_id: {...}} MAP on the per-user shape.
      const byModel = stats.by_model;
      const modelEntries: Array<[string, any]> =
        byModel && typeof byModel === "object" ? Object.entries(byModel) : [];
      if (modelEntries.length > 0) {
        for (const [modelId, sub] of modelEntries) {
          if (!sub || typeof sub !== "object") continue;
          const label = modelLabelFor(provider, mode, modelId);
          const existing = m.get(label);
          const subReqs = sub.requests ?? 0;
          const subSearches = sub.web_search_count ?? 0;
          const subCost = sub.cost_usd ?? sub.cost ?? 0;
          if (existing) {
            existing.requests += subReqs;
            existing.web_search_count += subSearches;
            existing.cost_usd += subCost;
          } else {
            m.set(label, { label, requests: subReqs, web_search_count: subSearches, cost_usd: subCost });
          }
        }
      } else {
        // Pre-overhaul bucket — no by_model map.
        const label = modelLabelFor(provider, mode, "");
        const existing = m.get(label);
        if (existing) {
          existing.requests += reqs;
          existing.web_search_count += searches;
          existing.cost_usd += cost;
        } else {
          m.set(label, { label, requests: reqs, web_search_count: searches, cost_usd: cost });
        }
      }
    }
  }

  return assembleOrdered(totalsBySection, modelsBySection);
}

/** Order sections by SECTION_ORDER, attach sorted model rows, drop empties. */
function assembleOrdered(
  totalsBySection: Map<string, { requests: number; web_search_count: number; cost_usd: number }>,
  modelsBySection: Map<string, Map<string, UsageModelEntry>>
): UsageSectionEntry[] {
  const out: UsageSectionEntry[] = [];
  for (const sect of SECTION_ORDER) {
    const totals = totalsBySection.get(sect);
    const models = modelsBySection.get(sect);
    const hasUsage =
      (totals && (totals.requests > 0 || totals.cost_usd > 0)) ||
      (models && models.size > 0);
    if (!hasUsage) continue;
    const modelList = models
      ? Array.from(models.values()).sort((a, b) => b.cost_usd - a.cost_usd)
      : [];
    out.push({
      section: sect,
      requests: totals?.requests ?? 0,
      web_search_count: totals?.web_search_count ?? 0,
      cost_usd: totals?.cost_usd ?? 0,
      models: modelList,
    });
  }
  return out;
}

// -----------------------------------------------------------------------------
// Renderer
// -----------------------------------------------------------------------------

const headerStyle: React.CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 10px",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "var(--text-primary)",
  textAlign: "left",
  fontSize: "12px",
};

const sectionBoxStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
  overflow: "hidden",
  background: "var(--bg)",
};

const modelRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "5px 10px 5px 26px",
  fontSize: "12px",
  borderTop: "1px solid var(--border)",
  color: "var(--text-secondary)",
};

/**
 * Render an ordered list of collapsible usage sections. Each section is
 * collapsed by default; clicking the header toggles a per-model breakdown.
 * Pass `compact` to shrink the spacing (admin per-user expander).
 */
export default function UsageSections({
  sections,
  compact = false,
}: {
  sections: UsageSectionEntry[];
  compact?: boolean;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  if (sections.length === 0) {
    return (
      <div
        style={{
          padding: "12px 0",
          textAlign: "center",
          color: "var(--text-tertiary)",
          fontSize: "12px",
        }}
      >
        No usage
      </div>
    );
  }

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: compact ? "5px" : "6px",
      }}
    >
      {sections.map((s) => {
        const isOpen = expanded.has(s.section);
        return (
          <div key={s.section} style={sectionBoxStyle}>
            <button
              type="button"
              onClick={() => toggle(s.section)}
              style={headerStyle}
              aria-expanded={isOpen}
            >
              <span
                style={{
                  fontSize: "10px",
                  color: "var(--text-tertiary)",
                  width: "10px",
                  display: "inline-block",
                }}
              >
                {isOpen ? "▾" : "▸"}
              </span>
              <span style={{ fontWeight: 600 }}>
                {sectionDisplayName(s.section)}
              </span>
              <span style={{ color: "var(--text-tertiary)" }}>·</span>
              <span style={{ color: "var(--text-secondary)" }}>
                {s.requests} req
              </span>
              <span style={{ color: "var(--text-tertiary)" }}>·</span>
              <span style={{ color: "var(--text-secondary)" }}>
                {s.web_search_count} searches
              </span>
              <span style={{ color: "var(--text-tertiary)" }}>·</span>
              <span
                style={{
                  marginLeft: "auto",
                  fontWeight: 600,
                  fontFamily: "ui-monospace, monospace",
                }}
              >
                {formatCost(s.cost_usd)}
              </span>
            </button>
            {isOpen && (
              <Fragment>
                {s.models.length === 0 ? (
                  <div
                    style={{
                      ...modelRowStyle,
                      color: "var(--text-tertiary)",
                      justifyContent: "flex-start",
                    }}
                  >
                    No per-model detail
                  </div>
                ) : (
                  s.models.map((m, i) => (
                    <div key={`${m.label}-${i}`} style={modelRowStyle}>
                      <span style={{ flex: 1, minWidth: 0 }}>{m.label}</span>
                      <span
                        style={{
                          color: "var(--text-tertiary)",
                          marginRight: "12px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {m.requests} req
                      </span>
                      <span
                        style={{
                          color: "var(--text-tertiary)",
                          marginRight: "12px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {m.web_search_count} searches
                      </span>
                      <span
                        style={{
                          fontFamily: "ui-monospace, monospace",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatCost(m.cost_usd)}
                      </span>
                    </div>
                  ))
                )}
              </Fragment>
            )}
          </div>
        );
      })}
    </div>
  );
}
